import axios from "axios";
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  setPostLoginRedirect,
} from "../Utils/storage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
  withCredentials: true,
});

const pendingRequests = new Map();

const getRequestKey = (method, url, params = null) => {
  let key = `${method}:${url}`;
  if (params) {
    try {
      key += `:${JSON.stringify(params)}`;
    } catch {
      key += ":unserializable";
    }
  }
  return key;
};

const dedupeRequest = (key, requestFn) => {
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }
  const promise = requestFn().finally(() => pendingRequests.delete(key));
  pendingRequests.set(key, promise);
  return promise;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true, timeout: 15000 },
      );

      const newAccess = data.accessToken;
      setAuthToken(newAccess);

      processQueue(null, newAccess);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      removeAuthToken();

      if (window.location.pathname !== "/login") {
        setPostLoginRedirect(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        window.location.href = "/login";
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export const getProducts = async (params = {}) => {
  try {
    const key = getRequestKey("GET", "/products", params);
    const response = await dedupeRequest(key, () =>
      api.get("/products", { params }),
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch products",
        success: false,
      }
    );
  }
};
export const getDeals = async () => {
  try {
    const key = getRequestKey("GET", "/deals");
    const response = await dedupeRequest(key, () => api.get("/deals"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch deals",
        success: false,
      }
    );
  }
};

export const getProductBySlug = async (slug) => {
  try {
    const key = getRequestKey("GET", `/products/${slug}`);
    const response = await dedupeRequest(key, () =>
      api.get(`/products/${slug}`),
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || { message: "Product not found", success: false }
    );
  }
};

export const getProductReviews = async (productId, { page = 1, limit = 50 } = {}) => {
  try {
    const params = { page, limit };
    const key = getRequestKey("GET", `/reviews/${productId}`, params);
    const response = await dedupeRequest(key, () =>
      api.get(`/reviews/${productId}`, { params }),
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Failed to fetch product reviews", success: false };
  }
};

export const createProductReview = async ({ productId, rating, comment }) => {
  try {
    const response = await api.post("/reviews", { productId, rating, comment });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Failed to submit review", success: false };
  }
};

export const validateDiscount = async (code) => {
  try {
    const response = await api.post("/discounts/validate", { code });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Invalid discount code",
        success: false,
      }
    );
  }
};

export const getMe = async () => {
  try {
    const key = getRequestKey("GET", "/auth/me");
    const response = await dedupeRequest(key, () => api.get("/auth/me"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch user data",
        success: false,
      }
    );
  }
};

export const getCategories = async () => {
  try {
    const key = getRequestKey("GET", "/categories");
    const response = await dedupeRequest(key, () => api.get("/categories"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch categories",
        success: false,
      }
    );
  }
};

export const getWishlist = async () => {
  try {
    const key = getRequestKey("GET", "/wishlist");
    const response = await dedupeRequest(key, () => api.get("/wishlist"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch wishlist",
        success: false,
      }
    );
  }
};

export const addToWishlist = async (productId) => {
  try {
    const response = await api.post("/wishlist", { productId });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to add to wishlist",
        success: false,
      }
    );
  }
};

export const removeFromWishlist = async (productId) => {
  try {
    const response = await api.delete(`/wishlist/${productId}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to remove from wishlist",
        success: false,
      }
    );
  }
};

export const LoginUser = async (formData) => {
  try {
    const response = await api.post("/auth/login", formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Login failed", success: false };
  }
};

export const logoutUser = async () => {
  try {
    const response = await api.post("/auth/logout");
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Logout failed", success: false };
  }
};

export const RegisterUser = async (userData) => {
  try {
    const response = await api.post("/auth/register", userData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || { message: "Registration failed", success: false }
    );
  }
};

export const autocompleteAddress = async (text) => {
  try {
    const response = await api.get("/address/autocomplete", {
      params: { text },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: "Failed to fetch addresses" };
  }
};

export const validateAddress = async (addressData) => {
  try {
    const response = await api.get("/address/validate", {
      params: addressData,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: "Failed to validate address" };
  }
};

export const getShippingAddresses = async () => {
  try {
    const response = await api.get("/address/shipping");
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch shipping addresses",
        success: false,
      }
    );
  }
};

export const addShippingAddress = async (addressData) => {
  try {
    const response = await api.post("/address/shipping", addressData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to add shipping address",
        success: false,
      }
    );
  }
};

export const updateShippingAddress = async (id, addressData) => {
  try {
    const response = await api.put(`/address/shipping/${id}`, addressData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to update shipping address",
        success: false,
      }
    );
  }
};

export const deleteShippingAddress = async (id) => {
  try {
    const response = await api.delete(`/address/shipping/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to delete shipping address",
        success: false,
      }
    );
  }
};

export const setDefaultShippingAddress = async (id) => {
  try {
    const response = await api.post(`/address/shipping/${id}/default`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to set default shipping address",
        success: false,
      }
    );
  }
};

export const verifyEmail = async (token) => {
  try {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || { message: "Verification failed", success: false }
    );
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post("/auth/forgot-password", { email });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to send reset email",
        success: false,
      }
    );
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post(`/auth/reset-password/${token}`, {
      newPassword,
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to reset password",
        success: false,
      }
    );
  }
};

export const resendVerification = async (email) => {
  try {
    const response = await api.post("/auth/resend-verification", { email });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to resend verification",
        success: false,
      }
    );
  }
};

export const updateProfile = async (userData) => {
  try {
    const response = await api.patch("/auth/me", userData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to update profile",
        success: false,
      }
    );
  }
};

export const getSettings = async () => {
  try {
    const key = getRequestKey("GET", "/settings");
    const response = await dedupeRequest(key, () => api.get("/settings"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch settings",
        success: false,
      }
    );
  }
};

export const updateSettings = async (settingsData) => {
  try {
    const response = await api.put("/settings", settingsData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to update settings",
        success: false,
      }
    );
  }
};

export const changePassword = async (passwordData) => {
  try {
    const response = await api.post("/settings/change-password", passwordData);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to change password",
        success: false,
      }
    );
  }
};

export const createCheckoutSession = async (orderData) => {
  if (!orderData || !orderData.items || !Array.isArray(orderData.items)) {
    throw new Error("Invalid order data");
  }

  try {
    const response = await api.post("/checkout/session", orderData);
    if (response.data && response.data.success !== false) {
      return response.data;
    } else {
      throw (
        response.data || {
          message: "Failed to create checkout session",
          success: false,
        }
      );
    }
  } catch (error) {
    const errorData = error.response?.data || {
      message: "Failed to create checkout session",
      success: false,
    };
    if (error.response?.status === 401) {
      setPostLoginRedirect(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      window.location.href = "/login";
    }
    throw errorData;
  }
};

export const getOrders = async () => {
  try {
    const key = getRequestKey("GET", "/orders");
    const response = await dedupeRequest(key, () => api.get("/orders"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch orders",
        success: false,
      }
    );
  }
};

export const getOrderById = async (id) => {
  try {
    const key = getRequestKey("GET", `/orders/${id}`);
    const response = await dedupeRequest(key, () => api.get(`/orders/${id}`));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch order details",
        success: false,
      }
    );
  }
};

export const cancelOrder = async (id) => {
  try {
    const response = await api.post(`/orders/${id}/cancel`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to cancel order",
        success: false,
      }
    );
  }
};

export const fetchFileBlob = async (url, { signal } = {}) => {
  try {
    const response = await api.get(url, {
      responseType: "blob",
      timeout: 30000,
      signal,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || new Error("Failed to fetch file");
  }
};

export const getTickets = async () => {
  try {
    const key = getRequestKey("GET", "/tickets");
    const response = await dedupeRequest(key, () => api.get("/tickets"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch tickets",
        success: false,
      }
    );
  }
};

export const getTicket = async (id) => {
  try {
    const key = getRequestKey("GET", `/tickets/${id}`);
    const response = await dedupeRequest(key, () => api.get(`/tickets/${id}`));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch ticket",
        success: false,
      }
    );
  }
};

export const createTicket = async (data) => {
  try {
    const response = await api.post("/tickets", data);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to create ticket",
        success: false,
      }
    );
  }
};

export const replyToTicket = async (id, data) => {
  try {
    const response = await api.post(`/tickets/${id}/reply`, data);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to reply to ticket",
        success: false,
      }
    );
  }
};

export const closeTicket = async (id) => {
  try {
    const response = await api.patch(`/tickets/${id}/close`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to close ticket",
        success: false,
      }
    );
  }
};

export const uploadFile = async (formData) => {
  try {
    const response = await api.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to upload file",
        success: false,
      }
    );
  }
};

export const getNotificationCount = async () => {
  try {
    const key = getRequestKey("GET", "/notifications/count");
    const response = await dedupeRequest(key, () =>
      api.get("/notifications/count"),
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch notification count",
        success: false,
      }
    );
  }
};

export const getNotifications = async () => {
  try {
    const key = getRequestKey("GET", "/notifications/");
    const response = await dedupeRequest(key, () => api.get("/notifications/"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch notifications",
        success: false,
      }
    );
  }
};

export const getHomeNotifications = async () => {
  try {
    const key = getRequestKey("GET", "/api/home/notifications");
    const response = await dedupeRequest(key, () =>
      api.get("/home/notifications"),
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch home notifications",
        success: false,
      }
    );
  }
};

export const getHomeProducts = async () => {
  try {
    const key = getRequestKey("GET", "/api/home/products");
    const response = await dedupeRequest(key, () => api.get("/home/products"));
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch home products",
        success: false,
      }
    );
  }
};

export const getHomeProductsByCategory = async (categoryId) => {
  try {
    const key = getRequestKey("GET", `/home/categories/${categoryId}/products`);
    const response = await dedupeRequest(key, () =>
      api.get(`/home/categories/${categoryId}/products`),
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to fetch home products by category",
        success: false,
      }
    );
  }
};

export const markNotificationRead = async (id) => {
  try {
    const response = await api.post(`/notifications/mark-read/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "Failed to mark notification as read",
        success: false,
      }
    );
  }
};

export default api;
